/**
 * Background-job completion text is delivered by the assistant continuation.
 * Keep the package's completion rows in the store for its internal lifecycle,
 * but do not render them as a second user-facing result.
 */
type MessageForDisplay = {
  role?: string;
  visible_in_chat?: boolean;
  metadata?: unknown;
  toolExecutions?: Array<{
    result?: unknown;
    error?: string;
    status?: string;
  }>;
};

export function prepareChatMessagesForDisplay<T extends MessageForDisplay>(
  messages: T[],
): T[] {
  return messages
    .filter((message) => {
      const metadata = (message as { metadata?: unknown }).metadata;
      const isCompletionNotice =
        metadata !== null &&
        typeof metadata === "object" &&
        (metadata as Record<string, unknown>).completion_notice === true;

      return (
        message.visible_in_chat !== false &&
        message.role !== "job_completion" &&
        !isCompletionNotice
      );
    })
    .map((message) => {
      if (!message.toolExecutions?.length) return message;
      const toolExecutions = message.toolExecutions.map((execution) => {
        const result = execution.result;
        if (!result || typeof result !== "object" || Array.isArray(result)) {
          return execution;
        }
        const resultObject = result as Record<string, unknown>;
        if (
          resultObject.background !== true &&
          typeof resultObject.job_id !== "string"
        ) {
          return execution;
        }
        return {
          ...execution,
          result: {
            background: true,
            ...(typeof resultObject.job_id === "string"
              ? { job_id: resultObject.job_id }
              : {}),
            ...(execution.status === "completed" &&
            typeof resultObject.document_id === "string"
              ? { document_id: resultObject.document_id }
              : {}),
            ...(execution.status === "completed" &&
            typeof resultObject.document_title === "string"
              ? { document_title: resultObject.document_title }
              : {}),
            ...(execution.status === "completed" &&
            typeof resultObject.filename === "string"
              ? { filename: resultObject.filename }
              : {}),
            ...(execution.status === "completed" &&
            (typeof resultObject.version === "number" ||
              typeof resultObject.version === "string")
              ? { version: resultObject.version }
              : {}),
          },
          error: undefined,
        };
      });
      return { ...message, toolExecutions };
    });
}
